/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Dayjs } from "dayjs";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Button, Label, Spacer, Text, theme } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { ContractInlineDatePicker } from "~/components/Contract/ContractInlineEdit/ContractInlineDatePicker";
import {
  contractEditModeInitialState,
  ContractEditModeType,
  contractLoadingInitialState,
  ContractLoadingType,
} from "~/components/Contract/ContractItem";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarMultiSelect } from "~/components/SidebarForm/inputs/SidebarMultiSelect";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { Action } from "~/constants/actions";
import { contractTypeOptions } from "~/constants/contractTypeOptions";
import { customerCategoryList } from "~/constants/customerCategoryList";
import { DurationType } from "~/models";
import { SubmitContract } from "~/models/contracts";
import {
  checkEndsDate,
  checkSignedDate,
  checkStartsDate,
  isValidUrlSchema,
} from "~/tools";

type ContractCreateProps = {
  onSubmit: (values: SubmitContract, type: string) => Promise<void>;
};

type Val = {
  label: string;
  customerCategory: string[];
  durationType: string;
  link: string;
};

const StyledErrorText = styled(Text)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text02};
  align: left;
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
function ContractCreate({ onSubmit }: ContractCreateProps) {
  const [isDateLoading, setIsDateLoading] = React.useState<ContractLoadingType>(
    contractLoadingInitialState
  );

  const [isEditMode, setIsEditMode] = React.useState<ContractEditModeType>(
    contractEditModeInitialState
  );
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [signed, setSigned] = React.useState<Dayjs | null>(null);
  const [starts, setStarts] = React.useState<Dayjs | null>(null);
  const [ends, setEnds] = React.useState<Dayjs | null>(null);

  const [signedError, setSignedError] = React.useState<string | undefined>(
    undefined
  );
  const [startsError, setStartsError] = React.useState<string | undefined>(
    undefined
  );
  const [endsError, setEndsError] = React.useState<string | undefined>(
    undefined
  );

  React.useEffect(() => {
    setSigned(null);
    setStarts(null);
    setEnds(null);
  }, [isSidebarOpen]);

  const methods = useForm();
  const watcher = methods.watch();

  React.useEffect(() => {
    setSignedError(checkSignedDate(watcher.durationType, signed).isValidate);
  }, [watcher.durationType, signed]);

  React.useEffect(() => {
    setStartsError(
      checkStartsDate(watcher.durationType, starts, signed).isValidate
    );
  }, [watcher.durationType, starts, signed]);

  React.useEffect(() => {
    setEndsError(
      checkEndsDate(watcher.durationType, starts, signed, ends).isValidate
    );
  }, [watcher.durationType, starts, signed, ends]);

  function validContractDates() {
    return (
      !checkSignedDate(watcher.durationType, signed).isValidate &&
      !checkStartsDate(watcher.durationType, starts, signed).isValidate &&
      !checkEndsDate(watcher.durationType, starts, signed, ends).isValidate
    );
  }

  const onCreate = async (values: Val) => {
    if (!validContractDates()) return;
    setIsLoading(true);
    const prepareValues: SubmitContract = {
      label: values.label,
      categories: values.customerCategory,
      link: values.link,
      durationType: values.durationType,
      starts: starts ? starts : null,
      signed: signed ? signed : null,
      ends: values.durationType === DurationType.FIXED ? ends : null,
    };

    await onSubmit(prepareValues, Action.CREATE);
    setIsLoading(false);
    setIsSidebarOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "auto" }}
        icon="plus"
      >
        Add contract
      </Button>

      <SidebarForm
        title="Add contract"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onCreate}
      >
        <SidebarTextField name="label" label="Label" required />

        <SidebarMultiSelect
          name="customerCategory"
          label="Customer Category"
          tooltip="Customer Category for MC Customers"
          options={customerCategoryList}
        />

        <SidebarDropdown
          name="durationType"
          label="Duration Type"
          options={contractTypeOptions}
          required
        />

        <SidebarTextField
          name="contractLink"
          label="Contract Link"
          registerOptions={{
            validate: (link: string) =>
              isValidUrlSchema.safeParse(link).success,
          }}
        />

        <FlexContainer>
          <Label name="Signed At:" required />
          <ContractInlineDatePicker
            date={signed}
            setDate={setSigned}
            property="signed"
            isLoading={isDateLoading}
            setIsLoading={setIsDateLoading}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            left
          />
        </FlexContainer>
        <StyledErrorText> {signedError} </StyledErrorText>
        <Spacer height="0.5rem" />

        <FlexContainer>
          <Label name="Starts At:" required />
          <ContractInlineDatePicker
            date={starts}
            setDate={setStarts}
            property="starts"
            isLoading={isDateLoading}
            setIsLoading={setIsDateLoading}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            left
          />
        </FlexContainer>
        <StyledErrorText> {startsError} </StyledErrorText>

        {watcher.durationType !== DurationType.INDEFINITE && (
          <>
            <Spacer height="0.5rem" />
            <FlexContainer>
              <Label name="Ends at:" required />
              <ContractInlineDatePicker
                date={ends}
                setDate={setEnds}
                property="ends"
                isLoading={isDateLoading}
                setIsLoading={setIsDateLoading}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                left
              />
            </FlexContainer>
            <StyledErrorText> {endsError} </StyledErrorText>
          </>
        )}

        <SidebarFormButtons
          submitLoading={isLoading}
          submitEnabled={!!(watcher.label && validContractDates())}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { ContractCreate };
